import { useEffect, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, Check, Clock3, Crown, Gift, Package, RefreshCw, Zap } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import {
  cancelSubscription,
  createCheckoutSession,
  createPassCheckoutSession,
  downgradeSubscription,
  fetchOverview,
  fetchPlans,
  upgradeSubscription,
} from '../api';
import {
  getCurrentPlanId,
  getPlanPrice,
  getPlanVariantState,
  isFreePlan,
  isOneTimePlan,
  normalizeBillingCycle,
  resolvePlanAction,
  sortPlansByRegularPrice,
} from '../utils/planActions';
import { getSafeReturnUrl } from '../utils/returnUrl';
import {
  formatBillingDate,
  normalizeSubscription,
  SUBSCRIPTION_LIFECYCLES,
} from '../utils/subscriptionStatus';

const LOAD_TIMEOUT_MS = 15000;

const getPortalReturnUrl = () => getSafeReturnUrl(localStorage.getItem('returnUrl'));

const formatCurrency = (amount, currencyCode = 'INR', maximumFractionDigits = 0) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: currencyCode,
  maximumFractionDigits,
}).format(Number(amount) || 0);

const validatePlansResponse = (response) => {
  if (response?.status !== 'success' || !Array.isArray(response?.data?.plans)) {
    throw new Error('The plans response was incomplete.');
  }
  return response.data;
};

const validateOverviewResponse = (response) => {
  if (response?.status !== 'success' || !response?.data?.subscription) {
    throw new Error('The billing overview response was incomplete.');
  }
  return response.data;
};

const getErrorMessage = (error, fallback) => {
  if (error?.name === 'AbortError') return 'The request took too long. Please try again.';
  return error?.message || fallback;
};

function PlanIcon({ planName }) {
  const normalizedName = String(planName || '').toLowerCase();
  if (normalizedName.includes('pro')) return <Crown className="h-5 w-5" />;
  if (normalizedName.includes('advanced') || normalizedName.includes('premium')) return <Zap className="h-5 w-5" />;
  return <Package className="h-5 w-5" />;
}

function LoadingState({ label = 'Loading your plans...' }) {
  return (
    <div className="flex min-h-[420px] flex-1 items-center justify-center px-4">
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-purple-100 border-t-[#8c3af0]" />
        <div>
          <p className="font-bold text-slate-800">{label}</p>
          <p className="mt-1 text-sm text-slate-500">This may take a few seconds.</p>
        </div>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry, title = 'Unable to load plans' }) {
  return (
    <div className="flex min-h-[420px] flex-1 items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl border border-red-100 bg-white p-7 text-center shadow-lg shadow-slate-900/5">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500"><AlertCircle className="h-6 w-6" /></div>
        <h2 className="mt-4 font-serif text-xl font-bold text-slate-900">{title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-500">{message}</p>
        <button type="button" onClick={onRetry} className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl bg-[#8c3af0] px-5 py-3 text-sm font-bold text-white transition hover:bg-purple-700">
          <RefreshCw className="h-4 w-4" /> Try Again
        </button>
      </div>
    </div>
  );
}

function BillingToggle({ selectedFrequency, onChange }) {
  return (
    <div className="inline-flex rounded-xl border border-purple-100 bg-white p-1 shadow-sm" role="tablist" aria-label="Billing cycle">
      {['monthly', 'annual'].map((frequency) => (
        <button
          key={frequency}
          type="button"
          role="tab"
          aria-selected={selectedFrequency === frequency}
          onClick={() => onChange(frequency)}
          className={`min-w-[108px] rounded-lg px-4 py-2 text-[13px] font-bold capitalize transition-all ${selectedFrequency === frequency
            ? 'bg-[#8c3af0] text-white shadow-md shadow-purple-500/20'
            : 'text-slate-500 hover:bg-purple-50 hover:text-[#8c3af0]'}`}
        >
          {frequency}
        </button>
      ))}
    </div>
  );
}

function Feature({ text }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-purple-100 text-[#8c3af0]"><Check className="h-3 w-3" strokeWidth={3} /></span>
      <span className="text-[13px] font-medium leading-relaxed text-slate-600">{text}</span>
    </li>
  );
}

function PlanCard({ plan, selectedFrequency, currentPlan, currentFrequency, subscriptionLifecycle, canCancel, pendingAction, actionError, onAction, onCancel }) {
  const selectedPrice = getPlanPrice(plan, selectedFrequency);
  const monthlyPrice = getPlanPrice(plan, 'monthly');
  const isAnnual = selectedFrequency === 'annual';
  const { isCurrentTier, isCurrentVariant } = getPlanVariantState({
    targetPlan: plan,
    currentPlan,
    selectedFrequency,
    currentFrequency,
    subscriptionLifecycle,
  });
  const action = resolvePlanAction({ targetPlan: plan, currentPlan, selectedFrequency, currentFrequency, subscriptionLifecycle });
  const isThisActionPending = pendingAction?.planId === plan.plan_id && pendingAction?.type !== 'cancel';
  const isAnyActionPending = Boolean(pendingAction);
  const hasMonthlyOffer = !isAnnual && selectedPrice?.discounted_first_month_amount != null;
  const displayedAmount = hasMonthlyOffer ? selectedPrice.discounted_first_month_amount : selectedPrice?.amount;
  const regularAnnualAmount = (monthlyPrice?.amount || 0) * 12;
  const annualSavings = isAnnual && selectedPrice ? Math.max(0, regularAnnualAmount - selectedPrice.amount) : 0;
  const iconClass = plan.name.toLowerCase().includes('pro')
    ? 'bg-amber-500 shadow-amber-500/20'
    : plan.name.toLowerCase().includes('advanced')
      ? 'bg-[#8c3af0] shadow-purple-500/20'
      : 'bg-slate-700 shadow-slate-700/20';
  const isDisabled = isAnyActionPending || ['current', 'unavailable'].includes(action.type);
  const currentVariantLabel = subscriptionLifecycle === SUBSCRIPTION_LIFECYCLES.CANCELLATION_SCHEDULED
    ? 'Cancellation Scheduled'
    : 'Current Plan';
  const currentFrequencyLabel = normalizeBillingCycle(currentFrequency) === 'annual' ? 'annually' : 'monthly';

  return (
    <article className={`relative flex h-full flex-col overflow-hidden rounded-2xl bg-white p-5 shadow-[0_10px_35px_rgba(55,38,89,0.07)] lg:p-6 ${isCurrentVariant
      ? 'border-2 border-[#8c3af0] pt-11 shadow-[0_14px_40px_rgba(140,58,240,0.14)]'
      : isCurrentTier
        ? 'border border-purple-200'
        : 'border border-slate-200/80'}`}
    >
      {isCurrentVariant && <div className="absolute inset-x-0 top-0 bg-[#8c3af0] py-2 text-center text-[10px] font-extrabold uppercase tracking-[0.16em] text-white">{currentVariantLabel}</div>}

      <div className="mb-5">
        <div className={`mb-4 flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-lg ${iconClass}`}><PlanIcon planName={plan.name} /></div>
        <h3 className="font-serif text-xl font-bold text-slate-900 lg:text-2xl">{plan.name}</h3>
        <p className="mt-1 text-sm font-medium text-slate-500">{plan.monthly_credits} Try-ons every month</p>
        {isCurrentTier && !isCurrentVariant && (
          <p className="mt-2 text-xs font-bold text-[#8c3af0]">Current tier · billed {currentFrequencyLabel}</p>
        )}
      </div>

      <div className="mb-5 rounded-2xl border border-purple-100/80 bg-[#faf8ff] p-4">
        {selectedPrice ? (
          <>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              {(hasMonthlyOffer || annualSavings > 0) && <span className="text-base font-medium text-slate-400 line-through">{formatCurrency(hasMonthlyOffer ? selectedPrice.amount : regularAnnualAmount, plan.currency)}</span>}
              <span className="text-3xl font-black tracking-tight text-slate-950">{formatCurrency(displayedAmount, plan.currency)}</span>
              <span className="text-xs font-semibold text-slate-500">/{isAnnual ? 'year' : 'month'}</span>
            </div>
            <p className="mt-2 text-[13px] font-semibold text-[#8c3af0]">
              {isAnnual
                ? annualSavings > 0 ? `Save ${formatCurrency(annualSavings, plan.currency)} every year` : 'Billed annually'
                : hasMonthlyOffer ? 'First-month offer for eligible customers' : 'Billed monthly'}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">
              {isAnnual
                ? `${formatCurrency(selectedPrice.amount / 12, plan.currency, 2)}/month when billed annually`
                : hasMonthlyOffer
                  ? `${formatCurrency(selectedPrice.amount, plan.currency)}/month afterward · Save ${formatCurrency(selectedPrice.first_month_discount_amount, plan.currency)} initially`
                  : `${formatCurrency(selectedPrice.amount, plan.currency)} billed every month`}
            </p>
          </>
        ) : <p className="text-sm font-semibold text-slate-500">Pricing unavailable for this billing cycle.</p>}
      </div>

      <ul className="mb-6 flex-1 space-y-3">
        {plan.features.map((feature) => <Feature key={feature} text={feature} />)}
      </ul>

      <button
        type="button"
        disabled={isDisabled}
        onClick={() => onAction(plan, action)}
        className={`w-full rounded-xl px-4 py-3 text-[13px] font-bold transition-all disabled:opacity-60 ${action.type === 'current'
          ? 'cursor-default bg-purple-100 text-[#8c3af0]'
          : action.type === 'downgrade'
            ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            : 'bg-[#8c3af0] text-white shadow-md shadow-purple-500/20 hover:bg-purple-700'}`}
      >
        {isThisActionPending ? action.loadingLabel : action.label}
      </button>

      {isCurrentVariant && canCancel && (
        <button type="button" onClick={onCancel} disabled={isAnyActionPending} className="mt-2 py-1.5 text-xs font-semibold text-red-500 transition hover:text-red-600 hover:underline disabled:opacity-50">
          {pendingAction?.type === 'cancel' ? 'Canceling...' : 'Cancel Subscription'}
        </button>
      )}
      {actionError && <p className="mt-2 text-center text-xs font-semibold text-red-600">{actionError}</p>}
    </article>
  );
}

function AdditionalPlans({ freePlan, passPlan, currentPlanId, passExpiryDate, hasActivePass, pendingAction, actionError, onPassPurchase }) {
  const passPrice = getPlanPrice(passPlan, 'one-time');
  const isPassPending = pendingAction?.planId === passPlan?.plan_id;
  if (!freePlan && !passPlan) return null;

  return (
    <section className="mt-6 grid gap-4 lg:grid-cols-[0.8fr_1.2fr]" aria-label="Additional plans">
      {freePlan && (
        <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600"><Gift className="h-5 w-5" /></div>
            <div>
              <div className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-slate-400">{currentPlanId === freePlan.plan_id ? 'Current plan' : 'Always available'}</div>
              <h3 className="mt-1 font-serif text-xl font-bold text-slate-900">{freePlan.name} plan</h3>
              <p className="mt-1 text-sm text-slate-500">{freePlan.features?.[0] || `${freePlan.monthly_credits} Try-ons/month`} with no subscription charge.</p>
            </div>
          </div>
        </div>
      )}

      {passPlan && (
        <div className="rounded-2xl border border-purple-200 bg-[#111625] p-5 text-white shadow-lg shadow-purple-950/10">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-purple-200"><Clock3 className="h-5 w-5" /></div>
              <div>
                <div className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-purple-300">One-time purchase</div>
                <h3 className="mt-1 font-serif text-xl font-bold">{passPlan.name}</h3>
                <p className="mt-1 text-sm text-slate-300">{passPlan.features.join(' · ')}</p>
                {hasActivePass && <p className="mt-2 text-xs font-bold text-emerald-300">Active until {formatBillingDate(passExpiryDate)}</p>}
                {actionError && <p className="mt-2 text-xs font-semibold text-red-300">{actionError}</p>}
              </div>
            </div>
            <button type="button" disabled={Boolean(pendingAction) || !passPrice} onClick={() => onPassPurchase(passPlan)} className="shrink-0 rounded-xl bg-white px-5 py-3 text-sm font-extrabold text-[#6f2bbd] transition hover:bg-purple-50 disabled:opacity-60">
              {isPassPending ? 'Processing...' : passPrice ? `Buy for ${formatCurrency(passPrice.amount, passPlan.currency)}` : 'Unavailable'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function SuccessOverview({ overview, countdown }) {
  const { user } = overview;
  const subscription = normalizeSubscription(overview.subscription);
  const isCancellationScheduled = subscription.lifecycle === SUBSCRIPTION_LIFECYCLES.CANCELLATION_SCHEDULED;
  const billingDate = isCancellationScheduled ? subscription.cancellationEffectiveDate : subscription.nextBillingDate;
  const billingDateLabel = isCancellationScheduled ? 'Access Ends On' : 'Next Billing Date';
  const cycleLabel = subscription.frequency === 'annual' ? 'yr' : 'mo';
  const statusStyles = subscription.lifecycle === SUBSCRIPTION_LIFECYCLES.PAST_DUE
    ? 'bg-red-100 text-red-700'
    : isCancellationScheduled
      ? 'bg-amber-100 text-amber-700'
      : 'bg-emerald-100 text-emerald-700';

  return (
    <div className="flex min-h-full flex-1 items-center justify-center bg-[#f4f2ff] px-4 py-12 lg:px-8">
      <div className="relative flex w-full max-w-[550px] flex-col items-center overflow-hidden rounded-3xl border border-purple-100 bg-white p-6 shadow-2xl sm:p-8">
        <div className="absolute -left-12 -top-12 h-32 w-32 rounded-full bg-purple-300/30 blur-2xl" />
        <div className="absolute -bottom-12 -right-12 h-32 w-32 rounded-full bg-purple-400/20 blur-2xl" />
        <div className="relative mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500"><Check className="h-8 w-8" strokeWidth={3} /></div>
        <div className="mb-6 text-center">
          <h2 className="font-serif text-2xl font-bold text-gray-900">Payment Successful!</h2>
          <p className="mt-2 max-w-sm text-[13px] text-gray-500">Your latest billing details have been loaded successfully.</p>
        </div>

        <div className="mb-6 flex w-full flex-col gap-4 rounded-2xl border border-purple-100 bg-[#fcfbff] p-5 shadow-inner">
          <div className="flex items-center justify-between border-b border-purple-50/80 pb-3">
            <span className="font-mono text-xs font-bold uppercase tracking-wider text-gray-400">Subscriber Details</span>
            <span className={`flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${statusStyles}`}><span className="h-1.5 w-1.5 rounded-full bg-current" />{isCancellationScheduled ? 'Cancellation Scheduled' : subscription.status}</span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">
            <OverviewField label="Name" value={user?.name || 'Not available'} />
            <OverviewField label="Username" value={user?.username || 'Not available'} />
            <OverviewField label="Current Plan" value={subscription.planName} accent />
            <OverviewField label="Price Amount" value={`${formatCurrency(subscription.priceAmount, subscription.currency)}/${cycleLabel}`} />
            <OverviewField label={billingDateLabel} value={formatBillingDate(billingDate)} />
            <OverviewField label="TryOn Balance" value={`${subscription.currentTryonBalance} TryOns`} accent />
          </div>
        </div>

        <div className="flex w-full flex-col items-center gap-3">
          <div className="text-[13px] text-gray-600">Redirecting in <strong className="rounded border border-purple-100 bg-purple-50 px-2 py-0.5 text-gray-900">{countdown}s</strong></div>
          <div className="mt-2 flex w-full gap-3">
            <button type="button" onClick={() => { window.location.hash = '/'; }} className="flex-1 rounded-xl border border-purple-100 bg-purple-50 px-4 py-3 text-[13px] font-bold text-[#8c3af0] hover:bg-purple-100/80">View Billing Portal</button>
            <button type="button" onClick={() => window.location.assign(getPortalReturnUrl())} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#8c3af0] px-4 py-3 text-[13px] font-bold text-white shadow-md hover:bg-purple-700"><ArrowLeft className="h-4 w-4" /> Back to Miggus App</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OverviewField({ label, value, accent = false }) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="text-[11px] font-semibold uppercase leading-tight text-gray-400">{label}</span>
      <span className={`mt-0.5 truncate text-[14px] font-bold ${accent ? 'text-[#8c3af0]' : 'text-gray-800'}`}>{value}</span>
    </div>
  );
}

export default function Plans() {
  const [searchParams] = useSearchParams();
  const checkoutSucceeded = Boolean(searchParams.get('success'));
  const checkoutCanceled = Boolean(searchParams.get('canceled'));
  const finalizedCheckout = useRef(false);
  const [plans, setPlans] = useState([]);
  const [currentPlanId, setCurrentPlanId] = useState('');
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState('monthly');
  const [loadState, setLoadState] = useState('loading');
  const [loadError, setLoadError] = useState('');
  const [retryVersion, setRetryVersion] = useState(0);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [message, setMessage] = useState(() => checkoutCanceled ? { type: 'error', text: 'Payment canceled. Please try again when you are ready.' } : null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [successState, setSuccessState] = useState(checkoutSucceeded ? 'loading' : 'idle');
  const [successOverview, setSuccessOverview] = useState(null);
  const [successError, setSuccessError] = useState('');
  const [successRetryVersion, setSuccessRetryVersion] = useState(0);
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    if (!checkoutSucceeded && !checkoutCanceled) return;
    const cleanUrl = window.location.origin + window.location.pathname + window.location.hash.split('?')[0];
    window.history.replaceState({}, document.title, cleanUrl);
    if (checkoutCanceled) {
      localStorage.removeItem('pending_plan_id');
      localStorage.removeItem('pending_plan_frequency');
      localStorage.removeItem('pending_plan_action');
    }
  }, [checkoutCanceled, checkoutSucceeded]);

  useEffect(() => {
    if (checkoutSucceeded) return undefined;
    let active = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS);

    const loadData = async () => {
      try {
        const [plansResponse, overviewResponse] = await Promise.all([
          fetchPlans({ signal: controller.signal }),
          fetchOverview({ signal: controller.signal }),
        ]);
        const plansData = validatePlansResponse(plansResponse);
        const overviewData = validateOverviewResponse(overviewResponse);
        if (!active) return;
        const subscription = normalizeSubscription(overviewData.subscription);
        const freePlan = plansData.plans.find(isFreePlan);
        const subscriptionPlanId = [SUBSCRIPTION_LIFECYCLES.FREE, SUBSCRIPTION_LIFECYCLES.EXPIRED].includes(subscription.lifecycle)
          ? freePlan?.plan_id
          : subscription.planId;
        setPlans(plansData.plans);
        setCurrentPlanId(getCurrentPlanId(plansData.plans, subscriptionPlanId || plansData.current_plan_id));
        setCurrentSubscription(subscription);
        setSelectedFrequency(normalizeBillingCycle(subscription.frequency));
        setLoadState('success');
      } catch (error) {
        if (!active) return;
        console.error('Failed to load plans and billing overview', error);
        setLoadError(getErrorMessage(error, 'We could not load your plans.'));
        setLoadState('error');
      } finally {
        window.clearTimeout(timeout);
      }
    };

    loadData();
    return () => {
      active = false;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [checkoutSucceeded, retryVersion]);

  useEffect(() => {
    if (!checkoutSucceeded) return undefined;
    let active = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS);

    const loadConfirmedOverview = async () => {
      try {
        if (!finalizedCheckout.current) {
          finalizedCheckout.current = true;
          const pendingPlanId = localStorage.getItem('pending_plan_id');
          const pendingFrequency = localStorage.getItem('pending_plan_frequency') || 'monthly';
          if (pendingPlanId && !pendingPlanId.toLowerCase().includes('plan_2day')) {
            try {
              await upgradeSubscription(pendingPlanId, pendingFrequency);
            } catch (error) {
              console.error('Failed to finalize subscription after checkout', error);
            }
          }
          localStorage.removeItem('pending_plan_id');
          localStorage.removeItem('pending_plan_frequency');
          localStorage.removeItem('pending_plan_action');
        }

        const overviewData = validateOverviewResponse(await fetchOverview({ signal: controller.signal }));
        if (!active) return;
        setSuccessOverview(overviewData);
        setSuccessState('success');
        setCountdown(5);
      } catch (error) {
        if (!active) return;
        console.error('Failed to load checkout overview', error);
        setSuccessError(getErrorMessage(error, 'Your payment returned successfully, but the latest billing details could not be loaded.'));
        setSuccessState('error');
      } finally {
        window.clearTimeout(timeout);
      }
    };

    loadConfirmedOverview();
    return () => {
      active = false;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [checkoutSucceeded, successRetryVersion]);

  useEffect(() => {
    if (countdown === null) return undefined;
    if (countdown === 0) {
      window.location.assign(getPortalReturnUrl());
      return undefined;
    }
    const timer = window.setTimeout(() => setCountdown((previous) => previous - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  const retryLoading = () => {
    setLoadError('');
    setLoadState('loading');
    setRetryVersion((version) => version + 1);
  };

  const retrySuccessOverview = () => {
    setSuccessError('');
    setSuccessState('loading');
    setSuccessRetryVersion((version) => version + 1);
  };

  const refreshBillingState = async () => {
    const [plansResponse, overviewResponse] = await Promise.all([fetchPlans(), fetchOverview()]);
    const plansData = validatePlansResponse(plansResponse);
    const overviewData = validateOverviewResponse(overviewResponse);
    const subscription = normalizeSubscription(overviewData.subscription);
    const freePlan = plansData.plans.find(isFreePlan);
    const subscriptionPlanId = [SUBSCRIPTION_LIFECYCLES.FREE, SUBSCRIPTION_LIFECYCLES.EXPIRED].includes(subscription.lifecycle)
      ? freePlan?.plan_id
      : subscription.planId;
    setPlans(plansData.plans);
    setCurrentPlanId(getCurrentPlanId(plansData.plans, subscriptionPlanId || plansData.current_plan_id));
    setCurrentSubscription(subscription);
    setSelectedFrequency(normalizeBillingCycle(subscription.frequency));
  };

  const redirectToCheckout = (url, planId, frequency, actionType) => {
    if (!url) throw new Error('No checkout URL was returned.');
    localStorage.setItem('pending_plan_id', planId);
    localStorage.setItem('pending_plan_frequency', frequency);
    localStorage.setItem('pending_plan_action', actionType);
    window.location.assign(url);
  };

  const handlePlanAction = async (plan, action) => {
    if (['current', 'unavailable'].includes(action.type)) return;
    const frequency = action.type === 'purchase-pass' ? 'one-time' : selectedFrequency;
    setPendingAction({ planId: plan.plan_id, type: action.type, frequency });
    setActionError(null);
    setMessage(null);

    try {
      if (action.type === 'purchase-pass') {
        const response = await createPassCheckoutSession();
        redirectToCheckout(response.data?.url || response.url, plan.plan_id, frequency, action.type);
        return;
      }
      if (action.type === 'checkout') {
        const response = await createCheckoutSession(plan.plan_id, frequency);
        redirectToCheckout(response.data?.url || response.url, plan.plan_id, frequency, action.type);
        return;
      }

      const response = action.type === 'upgrade'
        ? await upgradeSubscription(plan.plan_id, frequency)
        : await downgradeSubscription(plan.plan_id, frequency);
      if (response?.status !== 'success') throw new Error(response?.message || 'The subscription update failed.');
      if (response.data?.requires_checkout) {
        redirectToCheckout(response.data?.url, plan.plan_id, frequency, action.type);
        return;
      }
      setMessage({ type: 'success', text: response.message || 'Your subscription was updated successfully.' });
      await refreshBillingState();
    } catch (error) {
      console.error(`Failed to ${action.type} subscription`, error);
      setActionError({ planId: plan.plan_id, text: getErrorMessage(error, 'Please try again.') });
    } finally {
      setPendingAction(null);
    }
  };

  const confirmCancel = async () => {
    setShowCancelModal(false);
    setPendingAction({ planId: currentPlanId, type: 'cancel', frequency: normalizeBillingCycle(currentSubscription?.frequency) });
    setActionError(null);
    try {
      const response = await cancelSubscription();
      if (response?.status !== 'success') throw new Error(response?.message || 'Cancellation failed.');
      setMessage({ type: 'success', text: response.message || 'Subscription cancellation was scheduled successfully.' });
      await refreshBillingState();
    } catch (error) {
      console.error('Failed to cancel subscription', error);
      setActionError({ planId: currentPlanId, text: getErrorMessage(error, 'Cancellation failed. Please try again.') });
    } finally {
      setPendingAction(null);
    }
  };

  if (checkoutSucceeded) {
    if (successState === 'loading') return <LoadingState label="Confirming your latest billing details..." />;
    if (successState === 'error') return <ErrorState title="Billing confirmation is taking longer than expected" message={successError} onRetry={retrySuccessOverview} />;
    return <SuccessOverview overview={successOverview} countdown={countdown} />;
  }
  if (loadState === 'loading') return <LoadingState />;
  if (loadState === 'error') return <ErrorState message={loadError} onRetry={retryLoading} />;

  const currentPlan = plans.find((plan) => plan.plan_id === currentPlanId) || plans.find((plan) => plan.is_current_plan) || null;
  const currentFrequency = normalizeBillingCycle(currentSubscription?.frequency || currentPlan?.billing_cycle);
  const recurringPaidPlans = sortPlansByRegularPrice(plans.filter((plan) => !isOneTimePlan(plan) && !isFreePlan(plan)), selectedFrequency);
  const freePlan = plans.find(isFreePlan);
  const passPlan = plans.find(isOneTimePlan);
  const isCancellationScheduled = currentSubscription?.lifecycle === SUBSCRIPTION_LIFECYCLES.CANCELLATION_SCHEDULED;
  const isPastDue = currentSubscription?.lifecycle === SUBSCRIPTION_LIFECYCLES.PAST_DUE;
  const isExpired = currentSubscription?.lifecycle === SUBSCRIPTION_LIFECYCLES.EXPIRED;
  const cancellationDate = formatBillingDate(
    currentSubscription?.cancellationEffectiveDate || currentSubscription?.nextBillingDate,
  );

  return (
    <div className="min-h-full px-4 py-8 lg:px-8 lg:py-10">
      <div className="mx-auto w-full max-w-[1080px]">
        <header className="mb-7 text-center">
          <div className="mb-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#8c3af0]">Choose Your Plan</div>
          <h2 className="font-serif text-[24px] font-bold text-gray-900 lg:text-[30px]">Scale your virtual try-on experience</h2>
          <p className="mx-auto mt-2 max-w-xl text-[13px] leading-relaxed text-gray-500 lg:text-[14px]">Compare regular monthly and annual options. Introductory offers never affect upgrade or downgrade decisions.</p>
          <div className="mt-5"><BillingToggle selectedFrequency={selectedFrequency} onChange={setSelectedFrequency} /></div>
        </header>

        {message && <div className={`mx-auto mb-5 max-w-xl rounded-xl border px-4 py-3 text-center text-sm font-semibold ${message.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`}>{message.text}</div>}

        {isCancellationScheduled && (
          <div className="mx-auto mb-5 max-w-2xl rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center text-sm font-semibold text-amber-800">
            Cancellation scheduled. Your plan and credits remain available until {cancellationDate}. Upgrading will replace the scheduled cancellation.
          </div>
        )}

        {isPastDue && (
          <div className="mx-auto mb-5 max-w-2xl rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm font-semibold text-red-700">
            Your subscription payment is past due. Recurring plan changes are unavailable until the billing issue is resolved. Please contact support for help.
          </div>
        )}

        {isExpired && (
          <div className="mx-auto mb-5 max-w-2xl rounded-xl border border-slate-200 bg-white px-4 py-3 text-center text-sm font-semibold text-slate-700">
            Your previous subscription has expired. Choose a paid plan below to subscribe again.
          </div>
        )}

        {recurringPaidPlans.length > 0 ? (
          <section className="grid grid-cols-1 items-stretch gap-5 md:grid-cols-3" aria-label={`${selectedFrequency} subscription plans`}>
            {recurringPaidPlans.map((plan) => (
              <PlanCard
                key={plan.plan_id}
                plan={plan}
                selectedFrequency={selectedFrequency}
                currentPlan={currentPlan}
                currentFrequency={currentFrequency}
                subscriptionLifecycle={currentSubscription?.lifecycle}
                canCancel={currentSubscription?.canCancel}
                pendingAction={pendingAction}
                actionError={actionError?.planId === plan.plan_id ? actionError.text : ''}
                onAction={handlePlanAction}
                onCancel={() => setShowCancelModal(true)}
              />
            ))}
          </section>
        ) : <ErrorState title="No paid plans are available" message="No recurring paid plans were returned for this billing cycle." onRetry={retryLoading} />}

        <AdditionalPlans
          freePlan={freePlan}
          passPlan={passPlan}
          currentPlanId={currentPlanId}
          passExpiryDate={currentSubscription?.passExpiryDate}
          hasActivePass={currentSubscription?.hasActivePass}
          pendingAction={pendingAction}
          actionError={actionError?.planId === passPlan?.plan_id ? actionError.text : ''}
          onPassPurchase={(plan) => handlePlanAction(plan, resolvePlanAction({ targetPlan: plan }))}
        />
      </div>

      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-[18px] font-bold text-gray-900">Cancel Subscription</h3>
            <p className="mb-6 mt-2 text-[13px] text-gray-500">Are you sure you want to cancel your subscription? Your plan and credits will remain active until {formatBillingDate(currentSubscription?.nextBillingDate, { fallback: 'the end of the current billing period' })}.</p>
            <div className="flex w-full items-center gap-3">
              <button type="button" onClick={() => setShowCancelModal(false)} className="flex-1 rounded-xl bg-gray-100 py-2.5 text-[13px] font-semibold text-gray-700 hover:bg-gray-200">Keep Plan</button>
              <button type="button" onClick={confirmCancel} className="flex-1 rounded-xl bg-red-500 py-2.5 text-[13px] font-semibold text-white hover:bg-red-600">Cancel Plan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
